import { useEffect, useId, useRef } from 'react'

import { useTheme } from '../hooks/useTheme'

type Props = {
  chart: string
  className?: string
  /** Short description for screen readers. */
  label?: string
  /** Smaller typography and padding for beside-text layouts. */
  compact?: boolean
}

export default function MermaidDiagram({
  chart,
  className = '',
  label = 'Technical diagram',
  compact = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const reactId = useId().replace(/:/g, '')
  const { theme } = useTheme()

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let cancelled = false

    void (async () => {
      const mermaid = (await import('mermaid')).default
      if (cancelled) return

      mermaid.initialize({
        startOnLoad: false,
        theme: theme === 'dark' ? 'dark' : 'neutral',
        securityLevel: 'strict',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        flowchart: { useMaxWidth: true },
        sequence: { useMaxWidth: true },
        themeVariables: {
          fontSize: compact ? '13px' : '15px',
        },
      })

      const graphId = `mmd-${reactId}-${theme}`
      try {
        const { svg } = await mermaid.render(graphId, chart)
        if (cancelled || !containerRef.current) return
        containerRef.current.innerHTML = svg
      } catch (err) {
        if (!cancelled && containerRef.current) {
          containerRef.current.textContent =
            err instanceof Error ? err.message : 'Could not render diagram.'
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [chart, reactId, theme, compact])

  const density = compact ? 'p-2 lg:p-3' : 'p-4'

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={label}
      className={`overflow-x-auto rounded-lg border border-outline-variant/15 bg-surface-container-low/50 text-center [--mermaid-font-family:inherit] [&_svg]:mx-auto [&_svg]:max-h-[min(28rem,58vh)] [&_svg]:w-full [&_svg]:max-w-full ${density} ${className}`}
    />
  )
}
